// ConfirmationModal.jsx

import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { loadStripe } from '@stripe/stripe-js';
import api from '../utils/api';
import styled from 'styled-components';
import { CircularProgress, TextField, Button } from '@mui/material';
import { useSelector } from 'react-redux';
import { media } from '../utils/media';
import { useSubscription } from '../hooks/useSubscription';
import PurchaseCreditsDisplay from './subscription/PurchaseCreditsDisplay';

const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY);

/**
 * Props:
 *  - show (boolean): Whether the modal is open
 *  - onHide (function): Closes the modal
 *  - videoIds (Array): IDs of the videos selected
 *  - thumbnails (Array): Thumbnails for display
 *  - duration (number)?: Optional if needed directly
 */
const ConfirmationModal = ({ show, onHide, videoIds = [], thumbnails = [], duration = 0 }) => {
  const navigate = useNavigate();
  const user = useSelector((state) => state.auth.user);
  const { currentSubscription, currentQuota, updateCreditsUsageData } = useSubscription();

  // Price & loading states
  const [price, setPrice] = useState(0);
  const [loading, setLoading] = useState(false);
  const [isFree, setIsFree] = useState(false);

  // Misc messages & error states
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');

  // Discount code states
  const [discountCode, setDiscountCode] = useState('');
  const [isDiscountValid, setIsDiscountValid] = useState(false);
  const [discountError, setDiscountError] = useState('');

  // We assume 15 minutes (900s) per clip
  const totalRequestedSeconds = useMemo(() => {
    const SECONDS_PER_CLIP = 900;
    return videoIds.length * SECONDS_PER_CLIP;
  }, [videoIds]);

  // Format HH:MM for user display
  const formatTime = useCallback((seconds) => {
    const hours = String(Math.floor(seconds / 3600)).padStart(2, '0');
    const minutes = String(Math.floor((seconds % 3600) / 60)).padStart(2, '0');
    return `${hours}:${minutes}`;
  }, []);

  const formattedTotalDuration = useMemo(
    () => formatTime(totalRequestedSeconds),
    [totalRequestedSeconds, formatTime]
  );

  // Data sanity check
  const isValidData = useMemo(() => videoIds.length > 0, [videoIds]);

  // Log current user subscription plan
  useEffect(() => {
    if (currentSubscription && user) {
      console.log('🔍 Current User Subscription Plan:', {
        userId: user.id,
        username: user.username,
        subscription: {
          id: currentSubscription.id,
          status: currentSubscription.status,
          plan: {
            id: currentSubscription.plan?.id,
            name: currentSubscription.plan?.name,
            planCode: currentSubscription.plan?.planCode,
            description: currentSubscription.plan?.description,
            storageLimitSeconds: currentSubscription.plan?.storageLimitSeconds,
            freePurchaseSecondsPerMonth: currentSubscription.plan?.freePurchaseSecondsPerMonth,
            price: currentSubscription.plan?.price,
            features: currentSubscription.plan?.features
          },
          startsAt: currentSubscription.startsAt,
          endsAt: currentSubscription.endsAt,
          autoRenew: currentSubscription.autoRenew
        },
        quota: currentQuota ? {
          limitSeconds: currentQuota.limitSeconds,
          usedSeconds: currentQuota.usedSeconds,
          remainingSeconds: currentQuota.remainingSeconds,
          limitCredits: currentQuota.limitCredits,
          usedCredits: currentQuota.usedCredits,
          remainingCredits: currentQuota.remainingCredits
        } : 'No quota data'
      });
    }
  }, [currentSubscription, currentQuota, user]);


  // Recalculate the price if the modal is open and we have valid data
  const calculatePrice = useCallback(async () => {
    if (!isValidData) return;

    try {
      setLoading(true);
      setError('');
      setMessage('');

      const response = await api.get('/videos/calculate-price', {
        params: {
          videoIds: videoIds.join(','),
          duration: totalRequestedSeconds,
        },
      });
      setPrice(response.data.price);
      setIsFree(Number(response.data.price) <= 0);
    } catch (err) {
      setError('Failed to calculate price. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [videoIds, totalRequestedSeconds, isValidData]);

  useEffect(() => {
    if (show && isValidData) {
      calculatePrice();
    }
  }, [show, isValidData, calculatePrice]);

  // Validate discount code
  const validateDiscountCodeHandler = useCallback(async () => {
    try {
      setLoading(true);
      setDiscountError('');
      setError('');
      setMessage('');

      const response = await api.get('/admin/discount-codes/validate', {
        params: {
          code: discountCode,
          videoIds: videoIds.join(','),
          totalRequestedDurationInSeconds: totalRequestedSeconds,
        },
      });

      if (response.data.valid) {
        setIsDiscountValid(true);
        setMessage('Discount code applied successfully!');
        setPrice(response.data.price); // new discounted price
      } else {
        setIsDiscountValid(false);
        setDiscountError('Invalid or expired discount code.');
      }
    } catch (err) {
      setIsDiscountValid(false);
      const errorMsg = err.response?.data?.message || 'Error validating discount code.';
      setDiscountError(errorMsg);
    } finally {
      setLoading(false);
    }
  }, [discountCode, videoIds, totalRequestedSeconds]);

  // Handle payment initiation
  const handlePayment = useCallback(async () => {
    if (!user) {
      const paymentData = { videoIds, thumbnails, duration: totalRequestedSeconds };
      sessionStorage.setItem('pendingPaymentData', JSON.stringify(paymentData));
      onHide();
      return navigate('/login', {
        state: { from: '/confirmation', videoIds, thumbnails, duration: totalRequestedSeconds },
      });
    }
  
    if (!isValidData) {
      setError('Cannot proceed to payment due to invalid data.');
      return;
    }

    try {
      setLoading(true);
      setMessage('');
      setError('');
  
      const payload = {
        videoIds,
        userId: user.id,
        totalRequestedDuration: totalRequestedSeconds,
        successUrl: `${window.location.origin}/dashboard/purchased-videos`,
        cancelUrl: `${window.location.origin}/confirmation`,
        discountCode: isDiscountValid ? discountCode : null,
      };
  
      const res = await api.post('/videos/payment/checkout-session', payload);
      const { id: sessionId, url, error: backendError } = res.data || {};
      
      // Debug logging to understand what backend is returning
      console.log('🔍 Backend checkout-session response:', {
        sessionId,
        url,
        backendError,
        fullResponse: res.data,
        userCredits: currentSubscription?.plan?.freePurchaseSecondsPerMonth,
        requestedSeconds: totalRequestedSeconds,
        hasEnoughCredits: currentSubscription?.plan?.freePurchaseSecondsPerMonth >= totalRequestedSeconds,
        userPlan: currentSubscription?.plan?.name,
        userPlanCode: currentSubscription?.plan?.planCode,
        subscriptionStatus: currentSubscription?.status
      });
      
      // Additional detailed logging
      console.log('📊 Purchase Details:', {
        videoIds,
        totalRequestedDuration: totalRequestedSeconds,
        userId: user.id,
        successUrl: `${window.location.origin}/dashboard/purchased-videos`,
        cancelUrl: `${window.location.origin}/confirmation`,
        discountCode: isDiscountValid ? discountCode : null
      });
      
      console.log('🎯 Backend Decision:', {
        shouldBeFree: !sessionId,
        redirectToStripe: !!sessionId,
        successUrl: url,
        reason: sessionId ? 'Stripe session created (paid purchase)' : 'No session (free/credit purchase)'
      });
  
      // Backend signaled an error
      if (backendError) {
        setError(backendError);
        return;
      }
  
      // FREE PATH: no Stripe session; backend already fulfilled; url is the successUrl+processId
      if (!sessionId) {
        // Update credits usage if this was a free purchase using credits
        if (isFree && totalRequestedSeconds > 0) {
          updateCreditsUsageData(totalRequestedSeconds);
        }
        
        if (url) {
          window.location.assign(url);
          return;
        }
        // Fallback if url is unexpectedly missing
        window.location.assign(`${window.location.origin}/dashboard/purchased-videos`);
        return;
      }
  
      // PAID PATH: go to Stripe Checkout
      const stripe = await stripePromise;
      if (!stripe) {
        setError('Stripe not initialized. Please refresh and try again.');
        return;
      }
  
      const { error: stripeErr } = await stripe.redirectToCheckout({ sessionId });
      if (stripeErr) {
        // If Stripe gives us a URL in the response (rare), use it; otherwise show the error
        if (url) {
          window.location.assign(url); // last-resort fallback
        } else {
          setError(`Error redirecting to checkout: ${stripeErr.message}`);
        }
      }
    } catch (err) {
      const errorMsg = err.response?.data?.message || 'Error initiating payment. Please try again.';
      setError(errorMsg);
    } finally {
      setLoading(false);
    }
  }, [
    user,
    isValidData,
    videoIds,
    thumbnails,
    discountCode,
    isDiscountValid,
    navigate,
    onHide,
    currentSubscription?.plan?.freePurchaseSecondsPerMonth,
    totalRequestedSeconds,
  ]);

  // If modal not shown, return null
  if (!show) return null;

  return (
    <ModalOverlay>
      <ModalContainer>
        <ModalHeader>
          <ModalTitle>Confirmation</ModalTitle>
          <CloseButton onClick={onHide}>&times;</CloseButton>
        </ModalHeader>

        <ModalContent>
          <TimeRange>Total Requested Duration: {formattedTotalDuration}</TimeRange>
          
          <OrderInfo>Videos will be combined in the order they are chosen</OrderInfo>

          {loading ? (
            <LoadingWrapper>
              <CircularProgress size={24} color="primary" />
              <LoadingText>Loading...</LoadingText>
            </LoadingWrapper>
          ) : (
            <PriceTag>Price: ${price.toFixed(2)}</PriceTag>
          )}
          
          {/* Purchase Credits Display */}
          {user && (
            <div style={{ marginBottom: '16px' }}>
              <PurchaseCreditsDisplay userId={user.id} compact={true} />
            </div>
          )}
          
          {isFree && (
            <SuccessMessage>Covered by your monthly credits — no charge today.</SuccessMessage>
          )}

          <DiscountContainer>
            <StyledTextField
              label="Discount Code"
              variant="outlined"
              value={discountCode}
              onChange={(e) => setDiscountCode(e.target.value)}
              disabled={loading}
              fullWidth
            />
            <ApplyButton
              variant="contained"
              onClick={validateDiscountCodeHandler}
              disabled={loading || !discountCode}
            >
              Apply
            </ApplyButton>
          </DiscountContainer>

          {discountError && <ErrorMessage>{discountError}</ErrorMessage>}
          {message && !discountError && <SuccessMessage>{message}</SuccessMessage>}
          {error && <ErrorMessage>{error}</ErrorMessage>}

          {thumbnails.length > 0 && (
            <ThumbnailContainer>
              <CourtImage
                src={thumbnails[0] || '/fallback-thumbnail.png'}
                alt="Selected Video Thumbnail"
              />
            </ThumbnailContainer>
          )}
        </ModalContent>

        <ModalFooter>
          <CancelButton variant="outlined" onClick={onHide}>
            Cancel
          </CancelButton>
          <PaymentButton onClick={handlePayment} disabled={loading || !isValidData}>
            {loading ? <CircularProgress size={24} color="inherit" /> : (isFree ? 'Confirm' : 'Proceed to Payment')}
          </PaymentButton>

        </ModalFooter>
      </ModalContainer>
    </ModalOverlay>
  );
};

export default ConfirmationModal;

/* -----------------------------------
   Styled Components
----------------------------------- */

const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: ${({ theme }) => theme.colors.overlay};
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
  backdrop-filter: blur(5px);
  padding: ${({ theme }) => theme.spacing.md};

  ${media.mobile`
    align-items: flex-end;
    padding: ${({ theme }) => theme.spacing.sm};
  `}
`;

const ModalContainer = styled.div`
  background-color: ${({ theme }) => theme.colors.primaryBackground};
  border-radius: ${({ theme }) => theme.borderRadius.lg};
  padding: ${({ theme }) => theme.spacing.xl};
  width: 90%;
  max-width: 600px;
  max-height: 90vh;
  overflow-y: auto;
  position: relative;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.2);
  border: 1px solid ${({ theme }) => theme.colors.border};

  ${media.mobile`
    width: 100%;
    border-radius: ${({ theme }) => theme.borderRadius.md}
      ${({ theme }) => theme.borderRadius.md} 0 0;
    padding: ${({ theme }) => theme.spacing.md};
    max-height: 85vh;
  `}
`;

const ModalHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: ${({ theme }) => theme.spacing.lg};
  padding-bottom: ${({ theme }) => theme.spacing.md};
  border-bottom: 1px solid ${({ theme }) => theme.colors.border};
`;

const ModalTitle = styled.h2`
  font-size: ${({ theme }) => theme.fontSizes['2xl']};
  color: ${({ theme }) => theme.colors.textPrimary};
  margin: 0;
  font-weight: 700;
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  font-size: ${({ theme }) => theme.fontSizes.xl};
  cursor: pointer;
  color: ${({ theme }) => theme.colors.textSecondary};
  padding: ${({ theme }) => theme.spacing.xs};
  width: 32px;
  height: 32px;
  border-radius: 50%;
  transition: all 0.3s ease;

  &:hover {
    background-color: ${({ theme }) => theme.colors.hover};
    color: ${({ theme }) => theme.colors.textPrimary};
  }
`;

const ModalContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing.lg};
`;

const LoadingWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.sm};
`;

const LoadingText = styled.span`
  color: ${({ theme }) => theme.colors.textSecondary};
  font-size: ${({ theme }) => theme.fontSizes.md};
`;

const TimeRange = styled.p`
  font-size: 1rem;
  color: ${({ theme }) => theme.colors.textPrimary};
  margin: 0;
`;

const PriceTag = styled.p`
  font-size: 1.25rem;
  font-weight: bold;
  color: ${({ theme }) => theme.colors.primary};
  margin: 0;
`;

const DiscountContainer = styled.div`
  display: flex;
  gap: ${({ theme }) => theme.spacing.md};
  width: 100%;
  max-width: 400px;

  ${media.mobile`
    flex-direction: column;
    gap: ${({ theme }) => theme.spacing.sm};
  `}
`;

const StyledTextField = styled(TextField)`
  flex: 1;

  .MuiOutlinedInput-root {
    border-radius: ${({ theme }) => theme.spacing.sm};
    background-color: ${({ theme }) => theme.colors.inputBackground};
    
    &:hover .MuiOutlinedInput-notchedOutline {
      border-color: ${({ theme }) => theme.colors.primary};
    }
    &.Mui-focused .MuiOutlinedInput-notchedOutline {
      border-color: ${({ theme }) => theme.colors.primary};
    }
  }
`;

const ApplyButton = styled(Button)`
  background-color: ${({ theme }) => theme.colors.accentOrange};
  color: white;
  padding: ${({ theme }) => theme.spacing.sm} ${({ theme }) => theme.spacing.lg};
  border-radius: ${({ theme }) => theme.spacing.sm};
  font-size: 0.9rem;
  transition: all 0.3s ease;
  min-width: 80px;
  &:hover {
    background-color: ${({ theme }) => theme.colors.accentOrangeDark};
    transform: translateY(-2px);
  }
  &:disabled {
    background-color: ${({ theme }) => theme.colors.disabled};
    cursor: not-allowed;
  }
`;

const ThumbnailContainer = styled.div`
  width: 100%;
  max-width: 400px;
  border-radius: ${({ theme }) => theme.spacing.sm};
  overflow: hidden;
  box-shadow: ${({ theme }) => theme.shadows.medium};
`;

const CourtImage = styled.img`
  width: 100%;
  height: auto;
  display: block;
`;

const ModalFooter = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: ${({ theme }) => theme.spacing.md};
  margin-top: ${({ theme }) => theme.spacing.xl};
  padding-top: ${({ theme }) => theme.spacing.lg};
  border-top: 1px solid ${({ theme }) => theme.colors.border};
`;

const CancelButton = styled(Button)`
  color: ${({ theme }) => theme.colors.textPrimary};
  border-color: ${({ theme }) => theme.colors.border};
  padding: ${({ theme }) => theme.spacing.sm} ${({ theme }) => theme.spacing.lg};
  border-radius: ${({ theme }) => theme.spacing.sm};
  font-size: 0.9rem;
  transition: all 0.3s ease;
  &:hover {
    background-color: ${({ theme }) => theme.colors.hover};
    border-color: ${({ theme }) => theme.colors.textPrimary};
  }
`;

const PaymentButton = styled(Button)`
  background-color: ${({ theme }) => theme.colors.accentBlue};
  color: #fff;
  padding: ${({ theme }) => theme.spacing.sm} ${({ theme }) => theme.spacing.lg};
  border-radius: ${({ theme }) => theme.spacing.sm};
  font-size: 0.9rem;
  transition: all 0.3s ease;
  min-width: 120px;
  &:hover {
    background-color: ${({ theme }) => theme.colors.accentBlueDark};
    transform: translateY(-2px);
  }
  &:disabled {
    background-color: ${({ theme }) => theme.colors.disabled};
    cursor: not-allowed;
  }
`;

const ErrorMessage = styled.p`
  color: ${({ theme }) => theme.colors.error || '#d32f2f'};
  text-align: center;
  margin-top: -5px;
  font-size: 0.9rem;
  padding: ${({ theme }) => theme.spacing.xs};
  background-color: ${({ theme }) => theme.colors.error}15;
  border-radius: ${({ theme }) => theme.borderRadius.sm};
  border: 1px solid ${({ theme }) => theme.colors.error};
`;

const SuccessMessage = styled.p`
  color: #28a745; /* success color */
  text-align: center;
  margin-top: -5px;
  font-size: 0.9rem;
  padding: ${({ theme }) => theme.spacing.xs};
  background-color: #28a74515;
  border-radius: ${({ theme }) => theme.borderRadius.sm};
  border: 1px solid #28a745;
`;

const OrderInfo = styled.p`
  font-size: 0.9rem;
  color: ${({ theme }) => theme.colors.accentTeal || '#00A693'};
  margin: 0;
  padding: 8px 12px;
  background-color: ${({ theme }) => theme.colors.accentTeal || '#00A693'}15;
  border-radius: 6px;
  border-left: 3px solid ${({ theme }) => theme.colors.accentTeal || '#00A693'};
  font-weight: 500;
  display: flex;
  align-items: center;
  gap: 8px;
  
  &::before {
    content: "ℹ";
    font-size: 1rem;
    font-weight: bold;
  }
`;
